import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, Plus, Copy } from 'lucide-react';

interface PermCell { resource: string; actions: Record<string, boolean>; scope: string }
interface Role {
  id: string; key?: string; name: string; description?: string;
  is_builtin?: boolean; is_phase2?: boolean;
  permissions: PermCell[];
}

const humanise = (s: string): string => s.replace(/_/g, ' ').replace(/\b(\w)/g, (c) => c.toUpperCase());

export const RolesSettings: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [meta, setMeta] = useState<{ resources: string[]; actions: string[]; scopes: string[] }>({ resources: [], actions: [], scopes: [] });
  const [activeId, setActiveId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '', clone_from_role_id: '' });

  const load = async (): Promise<void> => {
    const [r, m] = await Promise.all([api.get<Role[]>('/roles'), api.get('/roles/meta/resources-actions')]);
    setRoles(r.data); setMeta(m.data);
    if (!activeId && r.data.length) setActiveId(r.data[0].id);
  };
  // load intentionally not in deps — should only run once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const active = useMemo(() => roles.find(r => r.id === activeId), [roles, activeId]);

  const updateCell = (resource: string, action: string, value: boolean): void => {
    if (!active) return;
    const perms = active.permissions.map(p => p.resource === resource ? { ...p, actions: { ...p.actions, [action]: value } } : p);
    setRoles(roles.map(r => r.id === active.id ? { ...r, permissions: perms } : r));
  };
  const updateScope = (resource: string, scope: string): void => {
    if (!active) return;
    const perms = active.permissions.map(p => p.resource === resource ? { ...p, scope } : p);
    setRoles(roles.map(r => r.id === active.id ? { ...r, permissions: perms } : r));
  };

  const save = async (): Promise<void> => {
    if (!active) return;
    if (active.is_builtin && !reason.trim()) { toast.error('A reason is required when editing a built-in role.'); return; }
    try {
      await api.put(`/roles/${active.id}/permissions`, { permissions: active.permissions, reason: reason || undefined });
      toast.success('Permissions saved'); setReason(''); load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed');
    }
  };

  const create = async (): Promise<void> => {
    if (!newRole.name.trim()) return;
    try {
      await api.post('/roles', newRole);
      toast.success('Role created'); setCreating(false); setNewRole({ name: '', description: '', clone_from_role_id: '' });
      load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Create failed');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <Card className="h-fit">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm">Roles</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setCreating(!creating)}><Plus size={14} /> New</Button>
        </CardHeader>
        <CardContent className="p-2">
          {creating && (
            <div className="p-2 mb-2 bg-raptor-neutral/40 rounded-md space-y-2">
              <Input placeholder="Role name" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} />
              <Input placeholder="Description" value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} />
              <Select value={newRole.clone_from_role_id} onChange={(e) => setNewRole({ ...newRole, clone_from_role_id: e.target.value })}>
                <option value="">Clone from… (optional)</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
              <Button size="sm" variant="gradient" onClick={create} className="w-full">Create role</Button>
            </div>
          )}
          <div className="space-y-0.5">
            {roles.map(r => (
              <button key={r.id} onClick={() => setActiveId(r.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-body ${activeId === r.id ? 'bg-raptor-blue/15 text-raptor-navy font-semibold' : 'text-raptor-navy/70 hover:bg-raptor-neutral/50'}`}>
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate">{r.name}</span>
                  {r.is_builtin && <Badge variant="muted">built-in</Badge>}
                  {r.is_phase2 && <Badge variant="muted">Roadmap</Badge>}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{active?.name ?? 'Select a role'}</CardTitle>
          <CardDescription>{active?.description ?? '—'} · Enforced on every request by the platform, not the browser.</CardDescription>
        </CardHeader>
        <CardContent>
          {active && (
            <>
              {active.is_builtin && (
                <div className="mb-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm">
                  <b>Built-in role.</b> Any change must be justified with a reason. The change is written to the permanent permission-change log for audit.
                </div>
              )}
              <div className="overflow-auto max-h-[60vh] border border-border/60 rounded-md">
                <table className="w-full text-xs font-body">
                  <thead className="bg-raptor-neutral/50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-2 font-semibold text-raptor-navy">Resource</th>
                      {meta.actions.map(a => <th key={a} className="py-2 px-1 text-center text-raptor-navy/70">{humanise(a).replace(' ', '\u00A0')}</th>)}
                      <th className="py-2 px-2 text-left text-raptor-navy/70">Scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.permissions.map((p) => (
                      <tr key={p.resource} className="border-t border-border/60">
                        <td className="py-1 px-2 font-tabular text-raptor-navy">{p.resource}</td>
                        {meta.actions.map(a => (
                          <td key={a} className="py-1 px-1 text-center">
                            <input data-testid={`perm-${active.id}-${p.resource}-${a}`} type="checkbox" checked={Boolean(p.actions[a])} onChange={(e) => updateCell(p.resource, a, e.target.checked)} />
                          </td>
                        ))}
                        <td className="py-1 px-2">
                          <Select value={p.scope} onChange={(e) => updateScope(p.resource, e.target.value)} className="h-8 text-xs">
                            {meta.scopes.map(s => <option key={s}>{s}</option>)}
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {active.is_builtin && (
                <div className="mt-3">
                  <Label>Reason (required for built-in roles)</Label>
                  <Input data-testid="perm-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Tightening delete access on brokers after audit review" />
                </div>
              )}
              <div className="mt-4 flex justify-end"><Button data-testid="perm-save" variant="gradient" onClick={save}><Save size={16} /> Save permissions</Button></div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
